/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

type Themes = {
  branding: {
    navbarHeight: string;
    light: { element?: string };
    dark: { element?: string };
  };
  light: { [key: string]: string };
  dark: { [key: string]: string };
};

export type ConfigENV = {
  SERVER_CONFIG: {
    oidc: {
      provider: 'gitlab' | 'keycloak';
      issuer: string;
      clientId: string;
      realm?: string;
      minTokenValidity?: number;
    };
    apiPrefix: string;
    topic: string;
    apiUrl: string;
    defaultGroup: string;
    launcherSnippet?: string;
    filterTags: string[];
  };
  EXTERNAL_SERVICES: {
    SPACES?: {
      topic: string;
      healthzUrl: string;
      deployUrl: string;
    };
    TRIGGER?: {
      healthzUrl: string;
      apiUrl: string;
    };
  };
  THEMES: Themes;
};

const safeUrl = (url: string): string => {
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

export type ConfigOptions = {
  antflowServerConfig: ConfigENV['SERVER_CONFIG'];
  antflowExternalServices: ConfigENV['EXTERNAL_SERVICES'] | undefined;
  themes: ConfigENV['THEMES'] | undefined;
};

const env = import.meta.env;

const getEnvFallbackConfig = (): ConfigOptions => {
  let serverApiUrl = env.ANTFLOW_SERVER_API_URL;
  if (serverApiUrl) {
    serverApiUrl = safeUrl(serverApiUrl);
  }

  let triggerApiUrl = env.ANTFLOW_TRIGGER_API_URL;
  if (triggerApiUrl) {
    triggerApiUrl = safeUrl(triggerApiUrl);
  }
  const antflowTopic = env.ANTFLOW_SERVER_TOPIC || 'antflow';

  let triggerHealthzUrl = env.ANTFLOW_TRIGGER_HEALTHZ_URL;
  if (triggerHealthzUrl) {
    triggerHealthzUrl = safeUrl(triggerHealthzUrl);
  }
  let spacesHealthzUrl = env.ANTFLOW_SPACES_HEALTHZ_URL;
  if (spacesHealthzUrl) {
    spacesHealthzUrl = safeUrl(spacesHealthzUrl);
  }
  let spacesTopics = env.ANTFLOW_SPACES_TOPIC;
  if (spacesTopics) {
    spacesTopics = safeUrl(spacesTopics);
  }
  let spacesDeployUrl = env.ANTFLOW_SPACES_DEPLOY_URL;
  if (spacesDeployUrl) {
    spacesDeployUrl = safeUrl(spacesDeployUrl);
  }

  return {
    antflowServerConfig: {
      apiPrefix: '',
      topic: antflowTopic,
      apiUrl: serverApiUrl,
      defaultGroup: '',
      launcherSnippet: 'antflow-launcher config --api-url <api-url> --token <token>\nantflow-launcher run',
      filterTags: [],
      oidc: {
        issuer: env.ANTFLOW_OIDC_ISSUER_URL,
        clientId: env.ANTFLOW_OIDC_CLIENT_ID,
        provider: env.ANTFLOW_SERVER_OIDC_PROVIDER,
        realm: env.ANTFLOW_OIDC_REALM,
        minTokenValidity: env.ANTFLOW_OIDC_MIN_TOKEN_VALIDITY
          ? parseInt(env.ANTFLOW_OIDC_MIN_TOKEN_VALIDITY)
          : undefined
      }
    },
    antflowExternalServices: triggerApiUrl
      ? {
          SPACES: { topic: spacesTopics, healthzUrl: spacesHealthzUrl, deployUrl: spacesDeployUrl },
          TRIGGER: { apiUrl: triggerApiUrl, healthzUrl: triggerHealthzUrl }
        }
      : undefined,
    themes: undefined
  };
};

export const initializeApp = (): Promise<ConfigOptions | undefined> => {
  const path = env.MODE === 'production' ? '../js/config.js' : '../config.js';
  const modulepath = import.meta.resolve(path);

  return new Promise((resolve) => {
    import(/* @vite-ignore */ modulepath)
      .then((module) => {
        const mod = module as Record<string, unknown>;
        if (Object.hasOwn(mod, 'config')) {
          const { config } = mod as { config: ConfigENV };
          if (config) {
            const result: ConfigOptions = {
              antflowServerConfig: {
                ...config.SERVER_CONFIG,
                apiUrl: safeUrl(config.SERVER_CONFIG.apiUrl),
                oidc: {
                  ...config.SERVER_CONFIG.oidc,
                  issuer: safeUrl(config.SERVER_CONFIG.oidc.issuer)
                }
              },
              antflowExternalServices: config.EXTERNAL_SERVICES
                ? {
                    ...config.EXTERNAL_SERVICES,
                    TRIGGER: config.EXTERNAL_SERVICES.TRIGGER
                      ? {
                          apiUrl: safeUrl(config.EXTERNAL_SERVICES.TRIGGER.apiUrl),
                          healthzUrl: safeUrl(config.EXTERNAL_SERVICES.TRIGGER.healthzUrl)
                        }
                      : undefined,
                    SPACES: config.EXTERNAL_SERVICES.SPACES
                      ? {
                          ...config.EXTERNAL_SERVICES.SPACES,
                          deployUrl: safeUrl(config.EXTERNAL_SERVICES.SPACES.deployUrl),
                          healthzUrl: safeUrl(config.EXTERNAL_SERVICES.SPACES.healthzUrl)
                        }
                      : undefined
                  }
                : undefined,
              themes: config.THEMES
            };

            if (!result.antflowServerConfig?.apiUrl && env.PROD) {
              const fallback = getEnvFallbackConfig();
              if (fallback.antflowServerConfig) {
                resolve(fallback);
                return;
              }
            }
            resolve(result);
            return;
          }
        } else {
          const fallback = getEnvFallbackConfig();
          if (fallback.antflowServerConfig || fallback.antflowExternalServices) {
            resolve(fallback);
            return;
          }
        }
      })
      .catch((error) => {
        if (env.DEV) {
          resolve(getEnvFallbackConfig());
        }
        console.error('Error loading config module:', error);
        resolve(undefined);
      });
  });
};
